import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../../components/feature/Sidebar';
import { TopBar } from '../../components/feature/TopBar';

interface UserProfile {
  name: string;
  email: string;
  role: string;
  company: string;
  phone: string;
  lastLogin: string;
  apiToken: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  preferences: {
    language: string;
    timezone: string;
    theme: string;
    currency: string;
  };
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile>({
    name: 'Carlos Rodríguez',
    email: 'carlos.rodriguez@empresa.com',
    role: 'Administrador',
    company: 'Energía Solar S.A.',
    phone: '+56 9 1234 5678',
    lastLogin: '2024-01-15 14:30:25',
    apiToken: 'frn_live_abc123def456ghi789jkl012mno345pqr678stu901vwx234yz',
    notifications: {
      email: true,
      push: true,
      sms: false
    },
    preferences: {
      language: 'es',
      timezone: 'America/Santiago',
      theme: 'light',
      currency: 'CLP'
    }
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [saveStatus, setSaveStatus] = useState('');

  const handleSave = async () => {
    setSaveStatus('saving');
    // Simular guardado
    await new Promise(resolve => setTimeout(resolve, 1500));
    setSaveStatus('saved');
    setIsEditing(false);
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const handleLogout = () => {
    localStorage.removeItem('fronius_user');
    localStorage.removeItem('fronius_auth');
    localStorage.removeItem('is_authenticated');
    navigate('/login');
  };

  const generateNewToken = async () => {
    const newToken = 'frn_live_' + Math.random().toString(36).substring(2, 50);
    setProfile(prev => ({ ...prev, apiToken: newToken }));
    setSaveStatus('token-generated');
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSaveStatus('copied');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Mi Perfil" />
        <div className="p-6 space-y-6">

          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl p-8">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border-2 border-white/30 shadow-lg">
                  <i className="ri-user-line text-white text-3xl"></i>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white mb-1">{profile.name}</h1>
                  <div className="flex items-center gap-3 text-blue-100">
                    <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-medium border border-white/10">
                      {profile.role}
                    </span>
                    <span className="text-sm opacity-80">{profile.company}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="px-6 py-3 bg-red-500/20 hover:bg-red-500/30 backdrop-blur-md border border-red-500/30 text-white rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 group"
              >
                <i className="ri-logout-box-line group-hover:-translate-x-1 transition-transform"></i>
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-6">
                <div className="p-4 bg-gray-50/50 border-b border-gray-100">
                  <h2 className="font-bold text-gray-900">Configuración de Cuenta</h2>
                </div>
                <nav className="p-2 space-y-1">
                  {[
                    { id: 'profile', label: 'Perfil Personal', icon: 'ri-user-settings-line' },
                    { id: 'notifications', label: 'Notificaciones', icon: 'ri-notification-badge-line' },
                    { id: 'preferences', label: 'Preferencias', icon: 'ri-equalizer-line' },
                    { id: 'api', label: 'Token de API', icon: 'ri-key-2-line' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                    >
                      <i className={`${tab.icon} text-lg ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'}`}></i>
                      <span>{tab.label}</span>
                      {activeTab === tab.id && (
                        <i className="ri-arrow-right-s-line ml-auto text-blue-400"></i>
                      )}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 min-h-[600px]">

                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <div className="space-y-8 animate-fade-in">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-6">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Información Personal</h2>
                        <p className="text-gray-500 text-sm mt-1">Gestiona tu información básica y de contacto.</p>
                      </div>
                      <button
                        onClick={() => setIsEditing(!isEditing)}
                        className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${isEditing
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20'
                          }`}
                      >
                        <i className={`${isEditing ? 'ri-close-line' : 'ri-edit-line'}`}></i>
                        <span>{isEditing ? 'Cancelar Edición' : 'Editar Perfil'}</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Nombre Completo</label>
                        <div className="relative">
                          <i className="ri-user-smile-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <input
                            type="text"
                            value={profile.name}
                            onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Email Corporativo</label>
                        <div className="relative">
                          <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <input
                            type="email"
                            value={profile.email}
                            onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Empresa</label>
                        <div className="relative">
                          <i className="ri-building-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <input
                            type="text"
                            value={profile.company}
                            onChange={(e) => setProfile(prev => ({ ...prev, company: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                        <div className="relative">
                          <i className="ri-phone-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <input
                            type="tel"
                            value={profile.phone}
                            onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Rol del Sistema</label>
                        <div className="relative">
                          <i className="ri-shield-user-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <select
                            value={profile.role}
                            onChange={(e) => setProfile(prev => ({ ...prev, role: e.target.value }))}
                            disabled={!isEditing}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed appearance-none"
                          >
                            <option value="Administrador">Administrador</option>
                            <option value="Usuario">Usuario</option>
                            <option value="Técnico">Técnico</option>
                            <option value="Supervisor">Supervisor</option>
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                        </div>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="flex justify-end pt-6 border-t border-gray-100 animate-fade-in">
                        <button
                          onClick={handleSave}
                          disabled={saveStatus === 'saving'}
                          className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {saveStatus === 'saving' ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Guardando...</span>
                            </>
                          ) : (
                            <>
                              <i className="ri-save-3-line"></i>
                              <span>Guardar Cambios</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Notifications Tab */}
                {activeTab === 'notifications' && (
                  <div className="space-y-8 animate-fade-in">
                    <div className="border-b border-gray-100 pb-6">
                      <h2 className="text-xl font-bold text-gray-900">Preferencias de Notificación</h2>
                      <p className="text-gray-500 text-sm mt-1">Elige cómo quieres recibir las alertas importantes.</p>
                    </div>

                    <div className="grid gap-4">
                      {[
                        { key: 'email', label: 'Notificaciones por Email', desc: 'Recibir alertas y reportes por correo electrónico', icon: 'ri-mail-send-line', color: 'bg-blue-100 text-blue-600' },
                        { key: 'push', label: 'Notificaciones Push', desc: 'Notificaciones en tiempo real en el navegador', icon: 'ri-notification-3-line', color: 'bg-purple-100 text-purple-600' },
                        { key: 'sms', label: 'Notificaciones SMS', desc: 'Alertas críticas por mensaje de texto', icon: 'ri-message-2-line', color: 'bg-green-100 text-green-600' }
                      ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between p-5 bg-gray-50 rounded-xl border border-gray-100 hover:border-blue-200 transition-colors group">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${item.color}`}>
                              <i className={`${item.icon} text-xl`}></i>
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">{item.label}</h3>
                              <p className="text-sm text-gray-500">{item.desc}</p>
                            </div>
                          </div>
                          <div className="relative inline-block w-12 align-middle select-none transition duration-200 ease-in">
                            <input
                              type="checkbox"
                              id={`toggle-${item.key}`}
                              checked={profile.notifications[item.key as keyof typeof profile.notifications]}
                              onChange={() => setProfile(prev => ({
                                ...prev,
                                notifications: {
                                  ...prev.notifications,
                                  [item.key]: !prev.notifications[item.key as keyof typeof prev.notifications]
                                }
                              }))}
                              className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                              style={{
                                right: profile.notifications[item.key as keyof typeof profile.notifications] ? '0' : 'auto',
                                left: profile.notifications[item.key as keyof typeof profile.notifications] ? 'auto' : '0',
                                borderColor: profile.notifications[item.key as keyof typeof profile.notifications] ? '#3B82F6' : '#E5E7EB'
                              }}
                            />
                            <label
                              htmlFor={`toggle-${item.key}`}
                              className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${profile.notifications[item.key as keyof typeof profile.notifications] ? 'bg-blue-500' : 'bg-gray-300'
                                }`}
                            ></label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preferences Tab */}
                {activeTab === 'preferences' && (
                  <div className="space-y-8 animate-fade-in">
                    <div className="border-b border-gray-100 pb-6">
                      <h2 className="text-xl font-bold text-gray-900">Configuración Regional</h2>
                      <p className="text-gray-500 text-sm mt-1">Personaliza el idioma, zona horaria y formatos.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Idioma de la Interfaz</label>
                        <div className="relative">
                          <i className="ri-translate absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <select
                            value={profile.preferences.language}
                            onChange={(e) => setProfile(prev => ({
                              ...prev,
                              preferences: { ...prev.preferences, language: e.target.value }
                            }))}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none"
                          >
                            <option value="es">Español</option>
                            <option value="en">English</option>
                            <option value="de">Deutsch</option>
                            <option value="fr">Français</option>
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Zona Horaria</label>
                        <div className="relative">
                          <i className="ri-time-zone-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <select
                            value={profile.preferences.timezone}
                            onChange={(e) => setProfile(prev => ({
                              ...prev,
                              preferences: { ...prev.preferences, timezone: e.target.value }
                            }))}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none"
                          >
                            <option value="America/Santiago">Santiago (GMT-3/GMT-4)</option>
                            <option value="Europe/Madrid">Europa/Madrid (CET)</option>
                            <option value="Europe/London">Europa/Londres (GMT)</option>
                            <option value="America/New_York">América/Nueva York (EST)</option>
                            <option value="Asia/Tokyo">Asia/Tokio (JST)</option>
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Tema Visual</label>
                        <div className="relative">
                          <i className="ri-palette-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <select
                            value={profile.preferences.theme}
                            onChange={(e) => setProfile(prev => ({
                              ...prev,
                              preferences: { ...prev.preferences, theme: e.target.value }
                            }))}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none"
                          >
                            <option value="light">Claro</option>
                            <option value="dark">Oscuro</option>
                            <option value="auto">Automático</option>
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Moneda Principal</label>
                        <div className="relative">
                          <i className="ri-money-dollar-circle-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <select
                            value={profile.preferences.currency}
                            onChange={(e) => setProfile(prev => ({
                              ...prev,
                              preferences: { ...prev.preferences, currency: e.target.value }
                            }))}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none"
                          >
                            <option value="CLP">Peso Chileno ($)</option>
                            <option value="USD">Dólar ($)</option>
                            <option value="EUR">Euro (€)</option>
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* API Tab */}
                {activeTab === 'api' && (
                  <div className="space-y-8 animate-fade-in">
                    <div className="border-b border-gray-100 pb-6">
                      <h2 className="text-xl font-bold text-gray-900">Gestión de API</h2>
                      <p className="text-gray-500 text-sm mt-1">Administra tus credenciales de acceso para integraciones externas.</p>
                    </div>

                    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 shrink-0">
                        <i className="ri-shield-keyhole-line text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-bold text-blue-900 mb-1">Seguridad del Token</h3>
                        <p className="text-sm text-blue-800 leading-relaxed">
                          Este token otorga acceso completo a tu cuenta a través de la API de Fronius Solar.web.
                          Trátalo como si fuera tu contraseña y nunca lo compartas en repositorios públicos.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700">Token de Acceso Personal</label>
                      <div className="flex gap-3">
                        <div className="flex-1 relative">
                          <input
                            type={showApiToken ? 'text' : 'password'}
                            value={profile.apiToken}
                            readOnly
                            className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl font-mono text-sm text-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <button
                            onClick={() => setShowApiToken(!showApiToken)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <i className={`${showApiToken ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
                          </button>
                        </div>
                        <button
                          onClick={() => copyToClipboard(profile.apiToken)}
                          className="px-6 py-3 bg-gray-800 text-white rounded-xl font-medium hover:bg-gray-900 transition-colors flex items-center gap-2 shadow-lg shadow-gray-800/20"
                        >
                          <i className="ri-file-copy-line"></i>
                          <span>Copiar</span>
                        </button>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-gray-100">
                      <button
                        onClick={generateNewToken}
                        className="px-6 py-3 bg-white border border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors flex items-center gap-2"
                      >
                        <i className="ri-refresh-line"></i>
                        <span>Revocar y Generar Nuevo Token</span>
                      </button>
                      <p className="text-xs text-gray-400 mt-3">
                        * Al generar un nuevo token, el anterior dejará de funcionar inmediatamente.
                      </p>
                    </div>
                  </div>
                )}

                {/* Status Messages */}
                {saveStatus && (
                  <div className={`fixed bottom-6 right-6 px-6 py-4 rounded-xl shadow-2xl flex items-center space-x-3 z-50 animate-slide-up ${saveStatus === 'saved' ? 'bg-green-600 text-white' :
                    saveStatus === 'token-generated' ? 'bg-blue-600 text-white' :
                      saveStatus === 'copied' ? 'bg-gray-800 text-white' :
                        'bg-white text-gray-800 border border-gray-100'
                    }`}>
                    <i className={`${saveStatus === 'saved' ? 'ri-checkbox-circle-line' :
                      saveStatus === 'token-generated' ? 'ri-key-2-line' :
                        saveStatus === 'copied' ? 'ri-clipboard-line' :
                          'ri-loader-4-line animate-spin'
                      } text-xl`}></i>
                    <span className="font-medium">
                      {saveStatus === 'saved' ? 'Cambios guardados correctamente' :
                        saveStatus === 'token-generated' ? 'Nuevo token generado con éxito' :
                          saveStatus === 'copied' ? 'Copiado al portapapeles' :
                            'Guardando cambios...'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}